import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Plus, Pencil } from "lucide-react"
import { toast } from "sonner"
import { api, type ApiEnvelope, type PortfolioItem } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FieldError } from "@/components/ui/field-error"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { ConfirmDelete } from "@/components/admin/confirm-delete"
import { ImageUpload } from "@/components/admin/image-upload"

export const Route = createFileRoute("/admin/_authed/portfolio")({
  component: PortfolioPage,
})

type FormState = { title: string; description: string; url: string; order: number; is_active: boolean }
const empty: FormState = { title: "", description: "", url: "", order: 0, is_active: true }

function PortfolioPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<PortfolioItem[]>({
    queryKey: ["admin", "portfolio"],
    queryFn: async () => (await api.get<ApiEnvelope<PortfolioItem[]>>("/admin/portfolio")).data.data,
  })

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<PortfolioItem | null>(null)
  const [form, setForm] = useState<FormState>(empty)
  const [image, setImage] = useState<File | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "portfolio"] })
    queryClient.invalidateQueries({ queryKey: ["portfolio"] })
  }

  const save = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? (v ? "1" : "0") : String(v)))
      if (image) fd.append("image", image)
      if (editing) {
        fd.append("_method", "PUT")
        await api.post(`/admin/portfolio/${editing.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      } else {
        await api.post("/admin/portfolio", fd, { headers: { "Content-Type": "multipart/form-data" } })
      }
    },
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setFieldErrors({})
      toast.success(editing ? "Portfolio item updated." : "Portfolio item created.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/admin/portfolio/${id}`),
    onSuccess: () => {
      invalidate()
      toast.success("Portfolio item deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(empty)
    setImage(null)
    setFieldErrors({})
    setOpen(true)
  }
  const openEdit = (item: PortfolioItem) => {
    setEditing(item)
    setForm({ title: item.title, description: item.description, url: item.url ?? "", order: item.order, is_active: item.is_active })
    setImage(null)
    setFieldErrors({})
    setOpen(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Portfolio</h1>
          <p className="mt-1 text-sm text-muted-foreground">Featured work shown on the public portfolio page.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full">
              <Plus className="h-4 w-4" /> New item
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit portfolio item" : "New portfolio item"}</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  aria-invalid={!!fieldErrors.title}
                />
                <FieldError message={fieldErrors.title} />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  aria-invalid={!!fieldErrors.description}
                />
                <FieldError message={fieldErrors.description} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="url">Project URL</Label>
                  <Input
                    id="url"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    aria-invalid={!!fieldErrors.url}
                  />
                  <FieldError message={fieldErrors.url} />
                </div>
                <div>
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  />
                </div>
              </div>
              <ImageUpload label="Image" currentUrl={editing?.image_url} onChange={setImage} />
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} id="is_active" />
                <Label htmlFor="is_active">Active</Label>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="text-sm text-destructive">Couldn't load portfolio items.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(data ?? []).map((item) => (
              <div key={item.id} className="overflow-hidden rounded-2xl border border-border bg-card">
                <div className="aspect-[16/9] bg-secondary">
                  {item.image_url && <img src={item.image_url} alt={item.title} className="h-full w-full object-cover" />}
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <h3 className="font-semibold">{item.title}</h3>
                    <Badge variant={item.is_active ? "default" : "secondary"}>{item.is_active ? "Active" : "Hidden"}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                  <div className="mt-4 flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(item)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <ConfirmDelete itemLabel={item.title} onConfirm={() => remove.mutate(item.id)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
